import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileSpreadsheet, FileDown, Printer, ClipboardList, ChevronDown, Loader2, Layers,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api, { downloadUrl } from '../lib/api';
import { fmtMoney } from '../lib/format';
import { StatusPill } from '../components/StatusPill';
import { SectionTitle, EmptyState } from '../components/common';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { CATEGORIES } from '../lib/engine';

const SHORT = {
  MATERIAL: 'Material', VEHICLE: 'Vehicle', MANPOWER: 'Manpower', SUPERVISORS: 'Superv.',
  'TOOLS & EQUIPMENT': 'Tools', SAFETY: 'Safety', SUBCONTRACTOR: 'Subcon.', MISC: 'Misc',
};

const catSubtotal = (ci, name) => ci.costBoq.categories.find((c) => c.name === name)?.subtotal || 0;

export default function BidSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="grid h-[60vh] place-items-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;

  const { project, computed } = data;
  const cur = project.currency;
  const fmtAED = (v, d = 2) => fmtMoney(v, cur, d);
  const colTotal = (cat) => computed.saleItems.reduce((a, ci) => a + catSubtotal(ci, cat), 0);
  const revNo = project.currentRevNo || 1;

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(`/projects/${id}`)} className="grid h-8 w-8 place-items-center rounded-lg border hover:bg-muted"><ArrowLeft size={15} /></button>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-[hsl(var(--copper))]" />
            <h1 className="font-display text-2xl font-bold">Bid Summary</h1>
            <span className="rounded-full border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Internal · Engineers</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-num text-[hsl(var(--copper))]">{project.code}</span>{project.name}
            <span className="text-xs">· Rev {revNo}</span><StatusPill status={project.status} />
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}><Printer size={14} className="mr-1.5" />Print</Button>
          <Button variant="outline" size="sm" data-testid="bid-summary-export-pdf" onClick={() => window.open(downloadUrl(`/projects/${id}/bid-summary.pdf`), '_blank')}><FileDown size={14} className="mr-1.5" />PDF</Button>
          <Button size="sm" data-testid="bid-summary-export-xlsx" className="bg-[hsl(var(--brand))] text-white hover:brightness-110" onClick={() => window.open(downloadUrl(`/projects/${id}/bid-summary.xlsx`), '_blank')}><FileSpreadsheet size={14} className="mr-1.5" />Excel</Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Cost build-up per sale item by category — engineering budget only. No margins or sell prices. For internal engineering review.</p>

      {computed.saleItems.length === 0 ? (
        <EmptyState icon={Layers} title="No cost data yet" description="Add sale items and cost lines in the workspace to generate a bid summary." testid="bid-summary-empty"
          action={<Button onClick={() => navigate(`/projects/${id}`)}>Open Workspace</Button>} />
      ) : (
        <>
          {/* Category cost matrix */}
          <div className="erak-card overflow-hidden" data-testid="bid-summary-matrix">
            <div className="border-b px-4 py-3"><SectionTitle>Category Cost Matrix</SectionTitle></div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-xs">
                <thead><tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2.5">Item</th>
                  <th className="px-3 py-2.5">Description</th>
                  <th className="px-2 py-2.5 text-right">Qty</th>
                  {CATEGORIES.map((c) => <th key={c} className="px-2 py-2.5 text-right">{SHORT[c]}</th>)}
                  <th className="px-3 py-2.5 text-right">Total Cost</th>
                  <th className="px-3 py-2.5 text-right">Eff. Unit</th>
                </tr></thead>
                <tbody>
                  {computed.saleItems.map((ci) => (
                    <tr key={ci.itemId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2 font-num font-semibold text-[hsl(var(--copper))]">{ci.itemNo}</td>
                      <td className="max-w-[280px] truncate px-3 py-2" title={ci.description}>{ci.description}</td>
                      <td className="money px-2 py-2">{ci.qty}</td>
                      {CATEGORIES.map((c) => {
                        const v = catSubtotal(ci, c);
                        return <td key={c} className={cn('money px-2 py-2', !v && 'text-muted-foreground/40')}>{v ? fmtAED(v, 0) : '–'}</td>;
                      })}
                      <td className="money px-3 py-2 font-semibold">{fmtAED(ci.totalCost, 0)}</td>
                      <td className="money px-3 py-2">{fmtAED(ci.effectiveUnitCost, 0)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 bg-muted/50 font-semibold">
                    <td className="px-3 py-2.5" colSpan={2}>TENDER TOTAL</td>
                    <td />
                    {CATEGORIES.map((c) => <td key={c} className="money px-2 py-2.5">{fmtAED(colTotal(c), 0)}</td>)}
                    <td className="money px-3 py-2.5">{fmtAED(computed.directCost, 0)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Detailed breakdown */}
          <div className="space-y-3">
            <SectionTitle>Detailed Cost Breakdown</SectionTitle>
            <div className="grid gap-3 lg:grid-cols-2">
              {computed.saleItems.map((ci) => {
                const isOpen = open[ci.itemId] ?? true;
                return (
                  <div key={ci.itemId} className="erak-card overflow-hidden">
                    <button onClick={() => setOpen((o) => ({ ...o, [ci.itemId]: !isOpen }))}
                      className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30">
                      <div className="flex items-center gap-2 text-left">
                        <ChevronDown size={15} className={cn('transition-transform', !isOpen && '-rotate-90')} />
                        <span className="font-num text-xs font-semibold text-[hsl(var(--copper))]">{ci.itemNo}</span>
                        <span className="line-clamp-1 text-sm">{ci.description}</span>
                      </div>
                      <span className="money shrink-0 text-sm font-semibold">{fmtAED(ci.totalCost, 0)}</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t">
                          {ci.costBoq.categories.filter((c) => c.lines.length).map((cat) => (
                            <div key={cat.name} className="border-b last:border-0">
                              <div className="flex items-center justify-between bg-muted/30 px-4 py-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wide">{cat.name}</span>
                                <span className="money text-xs font-semibold">{fmtAED(cat.subtotal)}</span>
                              </div>
                              <table className="w-full text-xs">
                                <tbody>
                                  {cat.lines.map((ln) => (
                                    <tr key={ln.lineId} className="border-b border-dashed last:border-0">
                                      <td className="w-20 px-4 py-1 font-num text-[hsl(var(--copper))]">{ln.code || '—'}</td>
                                      <td className="px-2 py-1">{ln.description}</td>
                                      <td className="w-24 px-2 py-1 text-right text-muted-foreground">{ln.qtyPerUnit} {ln.unit}</td>
                                      <td className="money w-28 px-4 py-1">{fmtAED(ln.amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                          <div className="flex items-center justify-between px-4 py-2 text-xs">
                            <span className="text-muted-foreground">Effective unit cost (÷ {ci.qty})</span>
                            <span className="money font-semibold">{fmtAED(ci.effectiveUnitCost)}</span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
